import React, { useMemo, useState } from 'react';
import { Box, Button, Container, Divider, Drawer, Grid, IconButton, Typography } from '@mui/material';
import AddOutlined from '@mui/icons-material/AddOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import { useSelector } from 'react-redux';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import { RootState } from 'store';
import { countryWAPMapping } from 'constants/constants';
import { AirSimple, DestinationSimple, HotelOptionsSimple } from '../../types';
import {
	airlineNamesFromSegments,
	buildIncludedCategoriesSummary,
	getCityNameCleaned,
	GetPAXTotalsResponse,
	separadorDeMiles,
} from '../../helpers';
import { SIGN_GREEN, SIGN_GREEN_BG, SIGN_GREEN_BORDER, useAgencyBranding } from '../../branding';
import { QuotationDestinationMultipleRoomWithPrice, QuotationServiceSimple } from '../../types';
import { NewRoomPricesMultiDestinationPreviewCard } from '../RoomPricesMultiDestinationPreviewCard/new';
import { RoomCardsCarousel } from '../CardsCarousel';
import { IncludeMultiDestination } from '../IncludeMultiDestination';
import { ReserveCta } from '../SelectionSummary/ReserveCta';
import { PaymentTeaser } from '../PaymentTeaser';

/**
 * Resumen persistente de la selección para cotizaciones de UN destino.
 *
 * Es "la cuenta" de la cotización: hotel elegido + vuelo + habitaciones +
 * adicionales = total por todo el grupo. Antes de esto, una cotización simple
 * con 2 habitaciones nunca mostraba el total de la familia: el cliente tenía
 * que sumar las habitaciones de cabeza.
 *
 * Mismo lenguaje visual que el drawer de MultiSelectHotelsMultiDestination,
 * con la interacción propia de simple: acá no se combinan destinos, se elige
 * UNA opción (la selección vive en QuotationPreview).
 */

export const SIMPLE_SUMMARY_BAR_HEIGHT = 120;

interface Props {
	quotationId: number;
	isExternal?: boolean;
	destinations: DestinationSimple[];
	paxTotals: GetPAXTotalsResponse;
	airs: AirSimple[];
	flightIndexSelected: number | null;
	/** destinationIndex -> índice absoluto dentro de destination.hotelOptions */
	selectedHotelByDestination: Record<number, number>;
	exchangeRate: number;
	localCurrency: string;
	showLocalCurrency?: boolean;
	selectedOptionalsTotalUSD: number;
	selectedAssistanceTotalUSD: number;
	selectedAssistanceName?: string;
	/** Para el recap "Incluye:" del drawer. */
	manualServices: QuotationServiceSimple[];
}

interface SelectedOptionDetail {
	destination: DestinationSimple;
	option: HotelOptionsSimple;
}

const sumRoomsTotal = (option: HotelOptionsSimple): number =>
	option.rooms.reduce((sum, room) => sum + room.totalPrice, 0);

const sumRoomsSign = (option: HotelOptionsSimple): number =>
	option.rooms.reduce((sum, room) => sum + (room.sign?.totalSign || 0), 0);

export const SimpleSelectionSummary = (props: Props): JSX.Element | null => {
	const {
		quotationId,
		isExternal,
		destinations,
		paxTotals,
		airs,
		flightIndexSelected,
		selectedHotelByDestination,
		exchangeRate,
		localCurrency,
		showLocalCurrency,
		selectedOptionalsTotalUSD,
		selectedAssistanceTotalUSD,
		selectedAssistanceName,
		manualServices,
	} = props;

	const [openDetail, setOpenDetail] = useState(false);
	const [showAlternativeCurrency, setShowAlternativeCurrency] = useState(false);

	const { auth } = useSelector((state: RootState) => state);
	const countryWAP = countryWAPMapping[auth.country] || '59897683318';
	const isColombia = auth.country === 'CO';

	const branding = useAgencyBranding(isExternal);

	const selectedDetails: SelectedOptionDetail[] = useMemo(
		() =>
			destinations
				.map((destination, destinationIndex) => {
					const optionIndex = selectedHotelByDestination[destinationIndex];
					const option = optionIndex != null ? destination.hotelOptions[optionIndex] : undefined;
					return option ? { destination, option } : null;
				})
				.filter(Boolean) as SelectedOptionDetail[],
		[destinations, selectedHotelByDestination],
	);

	// Si todavía no hay selección resuelta (datos cargando), no mostramos nada.
	if (selectedDetails.length === 0) return null;

	const packageTotalUSD = selectedDetails.reduce((sum, { option }) => sum + sumRoomsTotal(option), 0);
	const signTotalUSD = selectedDetails.reduce((sum, { option }) => sum + sumRoomsSign(option), 0);
	const additionalTotalUSD = selectedOptionalsTotalUSD + selectedAssistanceTotalUSD;
	const grandTotalUSD = packageTotalUSD + additionalTotalUSD;

	// Resumen por habitación con la forma de multi: la misma card etiquetada
	// ("Precio por adulto", "Precio total x la habitación") sirve en ambos
	// drawers. En simple con varios destinos, cada habitación suma sus tramos.
	const roomsCount = paxTotals.totalPaxPerRoom.length;
	const roomsSummary: QuotationDestinationMultipleRoomWithPrice[] = Array.from(
		{ length: roomsCount },
		(_, roomIndex) => {
			const roomsAtIndex = selectedDetails.map(({ option }) => option.rooms[roomIndex]).filter(Boolean);
			const signs = roomsAtIndex.map((room) => room.sign).filter(Boolean);
			const sum = (getter: (room: (typeof roomsAtIndex)[number]) => number) =>
				roomsAtIndex.reduce((total, room) => total + getter(room), 0);
			return {
				boards: roomsAtIndex.map((room) => room.board),
				rateTypes: roomsAtIndex.map((room) => room.rateType),
				adtPrice: sum((room) => room.adtPrice),
				chdPrice: sum((room) => room.chdPrice),
				infPrice: sum((room) => room.infPrice),
				totalPrice: sum((room) => room.totalPrice),
				sign:
					signs.length > 0
						? {
								adtSign: signs.reduce((total, sign) => total + (sign?.adtSign || 0), 0),
								chdSign: signs.reduce((total, sign) => total + (sign?.chdSign || 0), 0),
								infSign: signs.reduce((total, sign) => total + (sign?.infSign || 0), 0),
								totalSign: signs.reduce((total, sign) => total + (sign?.totalSign || 0), 0),
						  }
						: null,
			};
		},
	);
	// Mismo resumen que las cards de la página, con los servicios de los
	// destinos elegidos más los manuales.
	const includedSummary = buildIncludedCategoriesSummary({
		hasFlight: airs.length > 0,
		hasAccommodation: true,
		serviceNames: [
			...selectedDetails.flatMap(({ destination }) => (destination.services || []).map((service) => service.name)),
			...(manualServices || []).map((service) => service.name),
		],
	});

	// Nombre del vuelo elegido para el resumen: la aerolínea, no "Itinerario
	// opción N · AR". Si el itinerario vino como imagen del GDS no hay
	// segmentos, y ahí sí caemos a la numeración (es lo único que hay, y
	// coincide con el "Opción de vuelo N" de las cards de la página).
	const selectedAir = flightIndexSelected != null ? airs[flightIndexSelected] : undefined;
	const selectedFlightLabel = selectedAir
		? airlineNamesFromSegments(selectedAir.data) || `Opción de vuelo ${(flightIndexSelected as number) + 1}`
		: '';

	const roomCardHotels = selectedDetails.map(({ option }) => ({
		hotelName: option.details?.name || option.hotelName,
		rooms: option.rooms,
	}));

	const totalPassengers = paxTotals.adults + paxTotals.child + paxTotals.infants;
	const totalRooms = paxTotals.totalPaxPerRoom.length;

	const formatUSD = (value: number) => `USD ${separadorDeMiles(value)}`;
	const formatLocal = (value: number) => `${localCurrency} ${separadorDeMiles(Math.round(value * exchangeRate))}`;
	const primary = (value: number) => (isColombia ? formatLocal(value) : formatUSD(value));
	const alternative = (value: number) => (isColombia ? formatUSD(value) : formatLocal(value));
	const canToggleCurrency = isColombia || !!showLocalCurrency;

	// Mensaje de WhatsApp con la selección completa, mismo formato que multi.
	const flightMessage =
		airs.length > 1 && flightIndexSelected != null ? `* Itinerario opción ${flightIndexSelected + 1}.\n` : '';
	const hotelsMessage = selectedDetails
		.map(
			({ destination, option }) =>
				`* ${destination.nights} noches en ${getCityNameCleaned(destination.cityName)} con el hotel ${
					option.details?.name || option.hotelName
				}.`,
		)
		.join('\n');
	const additionalMessage =
		additionalTotalUSD > 0
			? `\nAdicionales seleccionados:\n${
					selectedOptionalsTotalUSD > 0 ? `* Opcionales agregados: USD ${selectedOptionalsTotalUSD}.\n` : ''
			  }${
					selectedAssistanceTotalUSD > 0
						? `* Asistencia ${selectedAssistanceName || ''}: USD ${selectedAssistanceTotalUSD}.`
						: ''
			  }`
			: '';
	const optionMessage = `Hola, quiero avanzar con la cotización n* ${quotationId}:\n${flightMessage}${hotelsMessage}${additionalMessage}`;
	const moveForwardLink = `https://wa.me/${countryWAP}?text=${encodeURIComponent(optionMessage)}`;

	return (
		<>
			{/* ── Barra fija inferior ─────────────────────────────────────── */}
			<Box
				sx={{
					position: 'fixed',
					bottom: 0,
					left: 0,
					right: 0,
					zIndex: (theme) => theme.zIndex.drawer - 1,
					// Fondo OPACO: translúcido obliga a mezclar con el contenido que pasa
					// por detrás en cada frame (y deja ver cualquier repintado tardío).
					backgroundColor: '#fff',
					borderTopLeftRadius: 14,
					borderTopRightRadius: 14,
					boxShadow: '0 -10px 30px rgba(49, 25, 75, 0.16)',
					paddingBottom: 'env(safe-area-inset-bottom)',
				}}
			>
				<Grid
					p={{ xs: 2, sm: 1.25 }}
					pb={{ xs: 2.5, sm: 1.25 }}
					paddingTop={{ xs: 1, sm: 1.25 }}
					justifyContent="space-between"
					alignItems="center"
					container
				>
					<Grid xs={12} sm="auto" item>
						<Typography
							component="h1"
							variant="h6"
							sx={{
								marginBottom: { xs: '4px', sm: 0 },
								width: { xs: '100%', sm: 'auto' },
								background: branding.headerGradient,
								paddingLeft: '16px',
								paddingRight: '16px',
								paddingTop: { xs: '8px', sm: '7px' },
								paddingBottom: { xs: '8px', sm: '7px' },
								borderRadius: '10px',
								fontSize: { xs: '1rem', sm: '0.9rem' },
								fontWeight: 700,
								color: 'white',
								boxShadow: '0 8px 20px rgba(49, 25, 75, 0.16)',
								whiteSpace: 'nowrap',
							}}
						>
							Tu selección:
						</Typography>
					</Grid>
					<Container
						maxWidth="md"
						sx={{
							display: 'flex',
							alignItems: 'center',
							flex: 1,
							gap: 1,
							paddingLeft: '8px',
							paddingRight: '8px',
						}}
					>
						<Grid xs item minWidth={0}>
							<Box minWidth={0} sx={{ overflow: 'hidden' }}>
								{/* El número protagonista es SIEMPRE el total por todo el grupo. */}
								<Typography component="p" fontWeight={900} color="primary" fontSize={12} lineHeight={1.1}>
									{additionalTotalUSD > 0 ? 'Total con adicionales' : 'Total del viaje'}
								</Typography>
								<Typography
									component="p"
									fontSize={{ xs: 19, sm: 20 }}
									fontWeight={900}
									color="primary"
									lineHeight={1.05}
									sx={{ mt: 0.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
								>
									{primary(grandTotalUSD)}
								</Typography>
								{signTotalUSD > 0 && (
									// El MONTO nunca se trunca (flexShrink 0): en pantalla angosta el ancho
									// lo cede la etiqueta, no el número — la seña es el gancho de venta.
									<Box display="flex" alignItems="baseline" gap={0.5} mt={0.25} sx={{ minWidth: 0 }}>
										<Typography
											component="span"
											fontSize={12}
											fontWeight={800}
											lineHeight={1.1}
											noWrap
											sx={{ color: SIGN_GREEN, flexShrink: 1, minWidth: 0 }}
										>
											{isColombia ? 'Reserva desde' : 'Seña'}
										</Typography>
										<Typography
											component="span"
											fontSize={12}
											fontWeight={800}
											lineHeight={1.1}
											sx={{ color: SIGN_GREEN, flexShrink: 0, whiteSpace: 'nowrap' }}
										>
											{primary(signTotalUSD)}
										</Typography>
									</Box>
								)}
								<Box display="flex" alignItems="center" gap={0.75} mt={0.4} flexWrap="wrap">
									<Typography component="span" fontSize={11} fontWeight={700} color="text.secondary" lineHeight={1.1}>
										{totalRooms > 1 ? `${totalRooms} habitaciones` : '1 habitación'} · {totalPassengers} pasajero
										{totalPassengers > 1 ? 's' : ''}
									</Typography>
									{canToggleCurrency && (
										<Button
											size="small"
											variant="text"
											onClick={() => setShowAlternativeCurrency((value) => !value)}
											sx={{
												p: 0,
												minWidth: 'auto',
												textTransform: 'none',
												fontSize: 10,
												fontWeight: 900,
												color: 'primary.main',
											}}
										>
											{showAlternativeCurrency ? 'Ocultar' : isColombia ? 'USD' : 'Moneda local'}
										</Button>
									)}
								</Box>
								{showAlternativeCurrency && (
									<Typography component="p" fontSize={11} fontWeight={700} color="text.secondary" lineHeight={1.1}>
										Total {alternative(grandTotalUSD)}
										{signTotalUSD > 0 && ` · Seña ${alternative(signTotalUSD)}`}
									</Typography>
								)}
							</Box>
						</Grid>

						<Grid item sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
							<Button
								variant="contained"
								color="secondary"
								href={moveForwardLink}
								rel="noopener noreferrer"
								target="_blank"
								startIcon={<WhatsAppIcon />}
								title="Le llega a tu asesor por WhatsApp. No es un pago."
								sx={{
									borderRadius: '999px',
									textTransform: 'none',
									fontWeight: 900,
									fontSize: 13,
									px: { xs: 1.1, sm: 1.6 },
									py: 0.9,
									minWidth: { xs: 78, sm: 92 },
									boxShadow: '0 8px 18px rgba(237, 102, 2, 0.22)',
									whiteSpace: 'nowrap',
								}}
							>
								Reservar
							</Button>
							<IconButton
								sx={{
									backgroundColor: 'secondary.main',
									color: 'white',
									width: 40,
									height: 40,
									'&:hover': { backgroundColor: 'secondary.main' },
									'&:active': { backgroundColor: 'secondary.main' },
								}}
								onClick={() => setOpenDetail(!openDetail)}
								aria-label={openDetail ? 'Cerrar detalle de tu selección' : 'Ver detalle de tu selección'}
							>
								{openDetail ? <ExpandMoreOutlined /> : <AddOutlined />}
							</IconButton>
						</Grid>
					</Container>
				</Grid>
			</Box>

			{/* ── Detalle expandido ───────────────────────────────────────── */}
			<Drawer
				anchor="bottom"
				open={openDetail}
				onClose={() => setOpenDetail(false)}
				ModalProps={{ keepMounted: true }}
				PaperProps={{
					sx: {
						maxHeight: '82vh',
						borderTopLeftRadius: 14,
						borderTopRightRadius: 14,
						backgroundColor: '#fbfafc',
						paddingBottom: 'env(safe-area-inset-bottom)',
					},
				}}
			>
				{/* Header sticky: título y cruz siempre a la vista mientras se scrollea
				    el detalle, como pasa en multi (donde la barra del drawer queda
				    fija). La salida tiene que estar visible en todo momento. */}
				<Box
					sx={{
						position: 'sticky',
						top: 0,
						zIndex: 2,
						backgroundColor: '#fbfafc',
						borderBottom: '1px solid rgba(49, 25, 75, 0.08)',
					}}
				>
					<Container maxWidth="md" sx={{ py: 1.25 }}>
						<Box display="flex" alignItems="center" justifyContent="space-between">
							<Typography variant="h6" color="primary" fontWeight={900}>
								Tu selección
							</Typography>
							<IconButton
								onClick={() => setOpenDetail(false)}
								aria-label="Cerrar detalle"
								size="small"
								sx={{ backgroundColor: 'rgba(49, 25, 75, 0.06)', color: 'primary.main' }}
							>
								<CloseIcon fontSize="small" />
							</IconButton>
						</Box>
					</Container>
				</Box>
				<Container maxWidth="md" sx={{ pt: 1.5, pb: 2 }}>
					{airs.length > 1 && selectedFlightLabel !== '' && (
						<Box mb={1.5}>
							<Typography variant="caption" color="text.secondary" fontWeight={700}>
								Vuelo
							</Typography>
							<Typography variant="body2" fontWeight={700}>
								{selectedFlightLabel}
							</Typography>
						</Box>
					)}

					{selectedDetails.map(({ destination, option }, detailIndex) => (
						<Box key={`selected-${detailIndex}`} mb={0.5}>
							<Typography variant="caption" color="text.secondary" fontWeight={700}>
								{destination.nights} noches en {getCityNameCleaned(destination.cityName)}
							</Typography>
							<Typography variant="body2" fontWeight={800} color="primary">
								{option.details?.name || option.hotelName}
							</Typography>
						</Box>
					))}

					{/* Detalle por habitación con etiquetas explícitas (la misma card que
					    usa multi): este público verifica, no escanea. El layout (1-2
					    visibles / 3+ carrusel con flechas) es el mismo criterio en
					    simple y multi. */}
					<RoomCardsCarousel>
						{roomsSummary.map((roomSummary, roomIndex) => (
							<NewRoomPricesMultiDestinationPreviewCard
								key={`room-card-${roomIndex}`}
								roomsSummaryPrice={roomSummary}
								indexRoom={roomIndex}
								hotels={roomCardHotels}
								paxTotals={paxTotals}
								oneRoomOnQuotation
								includedSummary={includedSummary}
								showUtility={false}
								markup={null}
								localCurrency={localCurrency}
								exchangeRate={exchangeRate}
								showLocalCurrency={canToggleCurrency && showAlternativeCurrency}
								primaryInLocalCurrency={isColombia}
								isExternal={isExternal}
							/>
						))}
					</RoomCardsCarousel>

					{additionalTotalUSD > 0 && (
						<Box mb={1.5} display="grid" gap={0.5}>
							{selectedOptionalsTotalUSD > 0 && (
								<Box display="flex" justifyContent="space-between" gap={1}>
									<Typography variant="body2" color="text.secondary">
										Opcionales agregados
									</Typography>
									<Typography variant="body2" fontWeight={800} color="secondary">
										+ {primary(selectedOptionalsTotalUSD)}
									</Typography>
								</Box>
							)}
							{selectedAssistanceTotalUSD > 0 && (
								<Box display="flex" justifyContent="space-between" gap={1}>
									<Typography variant="body2" color="text.secondary">
										Asistencia {selectedAssistanceName}
									</Typography>
									<Typography variant="body2" fontWeight={800} color="secondary">
										+ {primary(selectedAssistanceTotalUSD)}
									</Typography>
								</Box>
							)}
						</Box>
					)}

					<Divider sx={{ mb: 1.5 }} />

					<Box display="flex" justifyContent="space-between" alignItems="baseline" gap={1.5}>
						<Typography variant="body1" fontWeight={900} color="primary">
							Total por {totalPassengers} pasajero{totalPassengers > 1 ? 's' : ''}
						</Typography>
						<Typography variant="h5" fontWeight={900} color="primary" whiteSpace="nowrap">
							{primary(grandTotalUSD)}
						</Typography>
					</Box>
					{canToggleCurrency && (
						<Box display="flex" justifyContent="flex-end">
							<Typography variant="caption" color="text.secondary" fontWeight={700}>
								{alternative(grandTotalUSD)}
							</Typography>
						</Box>
					)}

					{signTotalUSD > 0 && (
						<Box
							mt={1.5}
							p={1.25}
							borderRadius="10px"
							bgcolor={SIGN_GREEN_BG}
							border={`1px solid ${SIGN_GREEN_BORDER}`}
							display="flex"
							justifyContent="space-between"
							alignItems="baseline"
							gap={1.5}
						>
							<Box>
								<Typography variant="body2" fontWeight={900} sx={{ color: SIGN_GREEN }}>
									Reservá ahora con seña
								</Typography>
								<Typography variant="caption" color="text.secondary">
									El resto lo coordinás con tu asesor. La seña puede variar al confirmar adicionales.
								</Typography>
							</Box>
							<Typography variant="body1" fontWeight={900} whiteSpace="nowrap" sx={{ color: SIGN_GREEN }}>
								{primary(signTotalUSD)}
							</Typography>
						</Box>
					)}

					{/* Cuadraditos de pago junto al total, igual que en multi. Config por
					    agencia en branding.ts (TN: 15 cuotas con monto calculado, sin
					    efectivo; PY: apunta a los planes por banco; etc.). */}
					<PaymentTeaser isExternal={isExternal} totalUSD={grandTotalUSD} formatPrice={primary} mt={1.5} />

					{/* Recap de valor justo antes del CTA, igual que en multi. */}
					<Typography
						component="h2"
						variant="h6"
						sx={{
							mt: 2.5,
							width: '100%',
							background: branding.headerGradient,
							px: '16px',
							py: '9px',
							borderRadius: '10px',
							fontSize: '1rem',
							fontWeight: 700,
							color: 'white',
						}}
					>
						Incluye:
					</Typography>
					<IncludeMultiDestination
						destinations={destinations}
						manualServices={manualServices}
						hasFlight={airs.length > 0}
					/>

					<ReserveCta href={moveForwardLink} />
				</Container>
			</Drawer>
		</>
	);
};

export default SimpleSelectionSummary;
